// Consent screen. Twin of the desktop consent renderer: fetch the form HTML,
// render it (sanitized), and offer agree / decline. Agree submits and the
// server advances; decline submits and the server ends the session (no
// completion code). A load failure does NOT auto-decline (that could wrongly
// end a session on a transient error); it surfaces the problem and lets the
// participant decline or retry.

import { useEffect, useState } from "react";
import type { WebRendererProps } from "./registry";
import type { ConsentResult } from "../api/types";
import { fetchForm } from "../session/sessionDriver";
import { Card, Button, Loading } from "../ui/primitives";
import {
  FormBlocksView,
  ParticipantGate,
  gateRecording,
  gateSatisfied,
  interactiveBlocks,
  parseFormContent,
  parseGate,
  type GateValues,
} from "./formBlocks";

export default function ConsentRenderer({ session, onDone }: WebRendererProps) {
  const [content, setContent] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [gateValues, setGateValues] = useState<GateValues>({});

  useEffect(() => {
    let alive = true;
    setContent(null);
    setFailed(false);
    setGateValues({});
    (async () => {
      try {
        const r = await fetchForm(session);
        if (alive) setContent(r.content);
      } catch {
        if (alive) setFailed(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [session, retryKey]);

  const submit = (result: ConsentResult) => onDone({ kind: "submit", result });

  if (failed) {
    return (
      <Card>
        <h1 className="jol-title">We could not load the consent form</h1>
        <p className="jol-body">
          Please try again. If it keeps failing you can decline to free your place.
        </p>
        <div className="jol-row">
          <Button variant="secondary" onClick={() => submit({ decision: "declined" })}>
            Decline
          </Button>
          <Button variant="primary" onClick={() => setRetryKey((k) => k + 1)}>
            Try again
          </Button>
        </div>
      </Card>
    );
  }

  if (content === null) return <Loading message="Loading the consent form..." />;

  const gate = parseGate(content);
  const blocks = parseFormContent(content);
  const satisfied = gateSatisfied(blocks, gateValues);
  return (
    <Card>
      <FormBlocksView blocks={blocks} />
      <ParticipantGate
        blocks={blocks}
        values={gateValues}
        onChange={(id, value) => setGateValues((v) => ({ ...v, [id]: value }))}
      />
      <div className="jol-row">
        <Button
          variant="secondary"
          style={gate?.decline_color ? { backgroundColor: gate.decline_color, color: "white" } : undefined}
          onClick={() => submit({ decision: "declined" })}
        >
          {gate?.decline_label ?? "I do not agree"}
        </Button>
        <Button
          variant="success"
          disabled={!satisfied}
          style={gate?.agree_color ? { backgroundColor: gate.agree_color, color: "white" } : undefined}
          onClick={() =>
            submit({
              decision: "agreed",
              ...(interactiveBlocks(blocks).length
                ? gateRecording(blocks, gateValues)
                : {}),
            })
          }
        >
          {gate?.agree_label ?? "I agree"}
        </Button>
      </div>
    </Card>
  );
}
